import React, { useState, useEffect } from "react";
import api from "../../services/api";
import ExecutionDetailModal from "../../components/ExecutionDetailModal";
import {
  Eye, CheckCircle, Clock, AlertTriangle, RefreshCw,
  DollarSign, Send, X, FileUp
} from "lucide-react";

const STATUS_CONFIG = {
  draft: { color: "bg-gray-100 text-gray-600", icon: Clock },
  submitted: { color: "bg-blue-50 text-blue-700", icon: Clock },
  under_review: { color: "bg-amber-50 text-amber-700", icon: Eye },
  rework_required: { color: "bg-orange-50 text-orange-700", icon: AlertTriangle },
  approved: { color: "bg-green-50 text-green-700", icon: CheckCircle },
  paid: { color: "bg-emerald-50 text-emerald-700", icon: DollarSign },
};

export default function AdminExecutions() {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showRework, setShowRework] = useState(false);
  const [reworkExec, setReworkExec] = useState(null);
  const [reworkItems, setReworkItems] = useState([]);
  const [questions, setQuestions] = useState([]);

  const load = () => {
    setLoading(true);
    api.getAdminExecutions(filter || undefined).then(setExecutions).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(load, [filter]);
  useEffect(() => { api.getQuestions().then(setQuestions).catch(console.error); }, []);

  const openDetail = async (ex) => {
    setDetailLoading(true);
    try {
      const data = await api.getAdminExecution(ex.id);
      setDetail(data);
    } catch (err) {
      console.error(err);
      // Fallback: show execution directly
      setDetail({ execution: ex, questions: [], milestones: [], rework_requests: [] });
    }
    setDetailLoading(false);
  };

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveComment, setApproveComment] = useState("");
  const [approveExecId, setApproveExecId] = useState(null);

  const handleApprove = async (id) => {
    setApproveExecId(id);
    setApproveComment("");
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    try {
      await api.approveExecution(approveExecId, approveComment);
      setShowApproveModal(false);
      setApproveExecId(null);
      load();
      setDetail(null);
    } catch (err) { alert(err.message); }
  };

  const handleMarkPaid = async (id) => {
    try { await api.markExecutionPaid(id); load(); setDetail(null); } catch (err) { alert(err.message); }
  };

  const handleDownloadPdf = () => {
    if (!detail?.execution) return;
    api.downloadPdf(api.getAdminPdfUrl(detail.execution.id)).catch(err => alert(err.message));
  };

  const openRework = (ex) => {
    setReworkExec(ex);
    setShowRework(true);
    setDetail(null);
    setReworkItems([{ question_id: "", reason: "", requires_file_upload: false }]);
  };

  const handleSendRework = async () => {
    const valid = reworkItems.filter(i => i.question_id && i.reason);
    if (valid.length === 0) return alert("Add at least one rework item with question and reason");
    try {
      await api.requestReworkQuestions(reworkExec.id, valid);
      setShowRework(false);
      setReworkItems([]);
      setReworkExec(null);
      load();
    } catch (err) { alert(err.message); }
  };

  const updateReworkItem = (idx, field, value) => {
    const items = [...reworkItems];
    items[idx] = { ...items[idx], [field]: value };
    setReworkItems(items);
  };

  const addReworkItem = () => setReworkItems([...reworkItems, { question_id: "", reason: "", requires_file_upload: false }]);
  const removeReworkItem = (idx) => setReworkItems(reworkItems.filter((_, i) => i !== idx));

  const statusBadge = (s) => {
    const conf = STATUS_CONFIG[s] || STATUS_CONFIG.draft;
    const Icon = conf.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${conf.color}`}>
        <Icon className="w-3 h-3" /> {(s || "draft").replace(/_/g, " ")}
      </span>
    );
  };

  const filters = ["", "submitted", "under_review", "rework_required", "approved", "paid"];

  // Build action buttons for the detail modal
  const getActions = () => {
    if (!detail?.execution) return [];
    const ex = detail.execution;
    const acts = [];
    if (ex.status === "submitted" || ex.status === "under_review") {
      acts.push({
        label: "Approve", icon: CheckCircle, testId: "approve-execution-btn",
        className: "bg-green-600 hover:bg-green-700 text-white",
        onClick: () => handleApprove(ex.id)
      });
      acts.push({
        label: "Return for Rework", icon: AlertTriangle, testId: "open-rework-btn",
        className: "bg-orange-600 hover:bg-orange-700 text-white",
        onClick: () => openRework(ex)
      });
    }
    if (ex.payment_status !== "paid" && (ex.status === "submitted" || ex.status === "approved")) {
      acts.push({
        label: "Mark as Paid", icon: DollarSign, testId: "mark-paid-btn",
        className: "bg-emerald-600 hover:bg-emerald-700 text-white",
        onClick: () => handleMarkPaid(ex.id)
      });
    }
    return acts;
  };

  return (
    <div data-testid="admin-executions-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Executions</h1>
        <button onClick={load} className="text-gray-400 hover:text-gray-600"><RefreshCw className="w-5 h-5" /></button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {filters.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f ? "bg-emerald-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"}`}>
            {f ? f.replace(/_/g, " ") : "All"}
          </button>
        ))}
      </div>

      {/* Detail Modal */}
      {detail && (
        <ExecutionDetailModal detail={detail} onClose={() => setDetail(null)} actions={getActions()} onDownloadPdf={handleDownloadPdf} />
      )}

      {/* Approve with Comment Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60]" onClick={() => setShowApproveModal(false)}>
          <div className="bg-white border border-gray-200 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Approve Execution</h2>
              <button onClick={() => setShowApproveModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-3">Optionally add a comment that will appear in the PDF report:</p>
            <textarea placeholder="Add a comment (optional)..." value={approveComment}
              onChange={e => setApproveComment(e.target.value)}
              data-testid="approve-comment-input"
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-green-500 resize-none h-24" />
            <div className="flex gap-2 mt-4">
              <button onClick={confirmApprove} data-testid="confirm-approve-btn"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" /> Confirm Approve
              </button>
              <button onClick={() => setShowApproveModal(false)}
                className="px-6 bg-gray-100 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Rework modal */}
      {showRework && reworkExec && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50" onClick={() => setShowRework(false)}>
          <div className="w-full max-w-2xl bg-white border border-gray-200 rounded-xl p-6 mx-4 max-h-[80vh] overflow-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Request Rework - {reworkExec.company_name}</h2>
              <button onClick={() => setShowRework(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              {reworkItems.map((item, idx) => (
                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Item {idx + 1}</span>
                    {reworkItems.length > 1 && (
                      <button onClick={() => removeReworkItem(idx)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                  <select value={item.question_id} onChange={e => updateReworkItem(idx, "question_id", e.target.value)}
                    data-testid={`rework-question-select-${idx}`}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500">
                    <option value="">Select question...</option>
                    {(reworkExec.answers || []).map(ans => {
                      const q = questions.find(qq => qq.id === ans.question_id);
                      return <option key={ans.question_id} value={ans.question_id}>{q?.question_text || ans.question_id}</option>;
                    })}
                  </select>
                  <textarea placeholder="Reason / Comment for the customer..." value={item.reason}
                    onChange={e => updateReworkItem(idx, "reason", e.target.value)}
                    data-testid={`rework-reason-${idx}`}
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500 resize-none h-16" />
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" checked={item.requires_file_upload}
                      onChange={e => updateReworkItem(idx, "requires_file_upload", e.target.checked)}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                    <FileUp className="w-3.5 h-3.5 text-amber-500" />
                    Request file upload from customer
                  </label>
                </div>
              ))}
            </div>

            <button onClick={addReworkItem} className="mt-3 text-sm text-emerald-600 hover:text-emerald-700 transition-colors font-medium">
              + Add another question
            </button>

            <div className="flex gap-2 mt-6">
              <button onClick={handleSendRework} data-testid="send-rework-btn"
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2">
                <Send className="w-4 h-4" /> Send Rework Request
              </button>
              <button onClick={() => setShowRework(false)}
                className="px-6 bg-gray-100 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {detailLoading && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl text-gray-500">Loading details...</div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="text-right px-6 py-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {executions.map(ex => (
                <tr key={ex.id} data-testid={`execution-row-${ex.id}`} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  onClick={() => openDetail(ex)}>
                  <td className="px-6 py-4 text-sm text-gray-900">{ex.company_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{ex.contact_email}</td>
                  <td className="px-6 py-4">{statusBadge(ex.status)}</td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-medium px-2 py-1 rounded ${ex.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                      {ex.payment_status || 'unpaid'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 font-medium">{(ex.total_score || 0).toFixed(1)}%</td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={(e) => { e.stopPropagation(); openDetail(ex); }} className="text-emerald-600 hover:text-emerald-700 p-1" data-testid={`view-execution-${ex.id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {executions.length === 0 && (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-400">No executions found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
